/* eslint-disable no-await-in-loop */
import { datadogRum } from '@datadog/browser-rum'
import { ApisauceInstance, create } from 'apisauce'
import { cloneDeep as _cloneDeep, get as _get, debounce } from 'lodash'
import {
  Balances,
  DatadogEncodingRun,
  DatadogEncodingRunDeviceManager,
  DecodeRequest,
  DecodedItem,
  IdentifierType,
  ItemIdentifier,
  Items,
  RfidAntennas,
  TmrBalance,
  TmrRfidAntenna,
  TmrTag,
  TmrWorkstation
} from 'stylewhere/api'
import { T, __ } from 'stylewhere/i18n'
import { AppStore, Sounds, Storage } from 'stylewhere/shared'
import { SystemError } from 'stylewhere/shared/errors'
import { OperationEncodingTagType } from 'stylewhere/types'
import { isProductionEnvironment, showToastError } from 'stylewhere/utils'

const WRITE_DEVICE_TYPE_READER = new Set([
  'nordic-id-sampo-native',
  'sensormatic',
  'impinj-speedway-octane',
  'zebra-fx-series'
])
const READERS_SUPPORTING_CUSTOM_COMMANDS = ['mediol-40', 'mediop-200u']
const READERS_SUPPORTING_KILL_TAG = ['feig-nfc', 'feig-cpr74']
const ANTENNA_PATH = '/api/v1/readers/antenna/'
const DEVICE_MANAGER_TIMEOUT = 3000
/** antenna start/stop commands are slower than the tag operations */
export const DEVICE_MANAGER_ANTENNA_TIMEOUT = 5000

/** sendWsCustomCommandForResponse got no (successful) answer within its timeoutMs */
export class CustomCommandTimeoutError extends Error {}

type TagOperationType = 'read' | 'write' | 'lock' | 'kill'

interface TagOperationConfig {
  /** device manager endpoint suffix (`/read`, `/write`, ...) */
  operation: TagOperationType
  /** query string parameters, in the order they must appear; empty/undefined values are skipped */
  queryParams: Record<string, string | number | undefined>
  tagType?: 'NFC' | 'UHF'
  /** use `writeTxPower` instead of `txPower` on the antennas sent in the payload */
  useWriteTxPower?: boolean
  antennaError?: string
  datadogSession?: DatadogEncodingRun
  datadogAction: string
  /** operation specific attributes merged into the datadog action */
  datadogAttributes?: (info: { apiDurationMs: number; ok: boolean }) => Record<string, any>
}

interface UpdateConfig {
  remoteUrl: string
  remotePath: string
  username: string
  password: string
  connectionType: 'SFTP'
  backupPaths?: string[]
  cleanupPatterns?: string[]
  excludePatterns?: string[]
}

interface DeviceManagerStatus {
  status: string
  telemetry: string
  lastUpdate: string
}

export interface RfidReaderStartOptions {
  onTagReadCallback?: (tag: TmrTag, antennaId?: string) => void
  onStop?: () => void
  connectionFailedCallback?: () => void
  restart?: boolean
  tagTypeToCheck?: OperationEncodingTagType
  customCommandsOperationType?: string
}

export interface RfidReaderStopOptions {
  manual?: boolean
  automatic?: boolean
  deviceType?: string
  customCommandsOperationType?: string
}

class RfidReader {
  /** use `getDeviceManagerApi()` instead of reading this directly: it guarantees the client is initialized */
  deviceManagerApi!: ApisauceInstance
  private deviceManagerApiUrl?: string
  deviceManagerStatusApi!: ApisauceInstance
  webSocket?: WebSocket[]
  sessionId = 'client1'
  wakeUpSocketTimeout?: NodeJS.Timeout
  workstation?: TmrWorkstation
  tags!: TmrTag[]
  decodeRequest?: DecodeRequest
  operationId?: string
  debounceDecodeTime?: number = 300

  timer?: any
  timerMs?: number = 5000
  automaticStop?: boolean = true

  batchInterval: boolean = false
  batchIntervalTime: number = 1000
  batchIntervalTagCount: number = 200
  batchIntervalTimer?: any
  batchIntervalTags: any[]

  isManualStop: boolean
  restartAntennas: boolean
  avoidRestartingTimerOnRead: boolean

  current_antenna = -1
  ws_configurations: string[] = []
  excludedAntennaIdsFromStopRequest: string[] = []
  detailsStatusAntennas: string[] = []

  restart_onerror_counter = 0
  restart_onerror_max = 4
  startInProgressPromise?: Promise<boolean>

  customCommandsWs?: WebSocket = undefined
  wsCustomKeepAliveTimeout?: NodeJS.Timeout

  ignoreReadingTagsWithPrefixes: string[] = []

  currentVersion: string = ''

  tagTypeToCheck: OperationEncodingTagType

  nfcReadConfiguration?: { active: boolean; block: number; blockCount: number }

  constructor() {
    this.batchIntervalTags = []
    this.isManualStop = false
    this.restartAntennas = false
    this.avoidRestartingTimerOnRead = false
    this.tagTypeToCheck = 'ALL'
  }

  setAutomaticStop = (val: boolean) => {
    this.automaticStop = val
  }

  setAutomaticStopTime = (time: number) => {
    this.timerMs = time
  }

  setAvoidRestartingTimerOnRead = (val: boolean) => {
    this.avoidRestartingTimerOnRead = val
  }

  setDebounceDecodeTime = (val: number) => {
    this.debounceDecodeTime = val
  }

  setBatchInterval = (val: boolean) => {
    this.batchInterval = val
  }

  setBatchIntervalTime = (val: number) => {
    this.batchIntervalTime = val
  }

  setBatchIntervalTagCount = (val: number) => {
    this.batchIntervalTagCount = val
  }

  setIgnoreReadingTagsWithPrefixes = (val: string) => {
    if (val !== '') {
      this.ignoreReadingTagsWithPrefixes = val.split(',')
    }
  }

  onTagReadCallback?: (tag: TmrTag, antennaId?: string) => void
  onStopCallback?: (automatic?: boolean) => void
  onStartCallback?: () => void
  onDecodedItemCallback?: (items: { [epc: string]: DecodedItem }, tags: TmrTag[]) => void
  onPendingTagsChangeAntennaButton?: (pendingTags: number) => void
  connectionFailedCallback?: () => void
  onStartCallbackAntennaButton?: () => void
  onStopCallbackAntennaButton?: () => void
  decodeFunction?: (
    identifierCodes: string[],
    types: (IdentifierType | undefined)[],
    antennaIds?: string[]
  ) => Promise<any>

  setNfcReadConfiguration = (conf?: { active: boolean; block: number; blockCount: number }) => {
    this.nfcReadConfiguration = conf
  }

  startTimer = () => {
    if (this.automaticStop) {
      this.stopTimer()
      this.timer = setTimeout(() => {
        this.stop({ manual: true, automatic: true })
      }, this.timerMs)
    }
  }

  stopTimer = () => {
    if (!this.timer) return
    clearTimeout(this.timer)
    this.timer = undefined
  }

  async initialize(customCommandsOperationType?: string) {
    try {
      this.onDecodedItemCallback = undefined
      this.onTagReadCallback = undefined
      this.decodeFunction = undefined
      this.decodeRequest = undefined
      this.nfcReadConfiguration = undefined
      this.tags = []

      // if antenna is reading stop then
      if (this.isReading()) {
        await this.turn_sequential('stop', undefined, undefined, customCommandsOperationType)
      }

      // check if workstation was already chosen
      if (AppStore.defaultWorkstation) {
        this.workstation = AppStore.defaultWorkstation
        return
      }

      if (!AppStore.workstations || AppStore.workstations.length === 0) throw new Error('No workstations found!')

      // set default workstation or select the first one
      const defaultCode = AppStore.loggedUser?.place?.attributes?.default_workstation_code ?? false
      this.workstation = AppStore.workstations?.find((ws) => ws.code === defaultCode)
    } catch (error) {
      if (!AppStore.getEmulation()) throw error
    }
  }

  setOnDecodedItemCallback(
    onDecodedItemCallback: (item: { [epc: string]: DecodedItem }, tag: TmrTag[]) => void,
    decodeRequest?: DecodeRequest
  ) {
    this.decodeRequest = decodeRequest
    this.onDecodedItemCallback = onDecodedItemCallback
  }

  setOnTagReadCallback(onTagReadCallback: (tag: TmrTag, antennaId?: string) => void) {
    this.onTagReadCallback = onTagReadCallback
  }

  clear() {
    this.tags = []
  }

  initUhfTags(identifiers: string[]) {
    const tags: TmrTag[] = []
    identifiers.forEach((idt) => {
      tags.push({
        type: 'UHF_TAG',
        epc: idt,
        uid: undefined,
        antennaId: '',
        timestamp: 0,
        antennaIdDb: '',
        channelIndex: 0,
        firstSeen: 0,
        inventoryParameterSpecID: 0,
        lastSeen: 0,
        peakRSSI: 0,
        roSpecID: 0,
        tagSeenCount: 0,
        code: '',
        pending: false,
        processing: false
      })
    })
    this.tags = tags
  }

  removeTags(identifiers: string[]) {
    this.tags = this.tags.filter((tag) =>
      tag.epc ? !identifiers.includes(tag.epc) : tag.uid ? !identifiers.includes(tag.uid) : true
    )
  }

  setDecodeFunction(
    decodeFunction: (
      identifierCodes: string[],
      types: (IdentifierType | undefined)[],
      antennaIds?: string[]
    ) => Promise<any>
  ) {
    this.decodeFunction = decodeFunction
  }

  isReading = (): boolean => {
    if (this.webSocket && this.webSocket.length > 0)
      return this.webSocket.some((ws) => ws?.readyState === WebSocket.OPEN)
    else return false
  }

  async emulateTag(rfidTag: Partial<TmrTag>) {
    this.onTagRead(JSON.stringify(rfidTag))
  }

  async emulateTags(tags: string[]) {
    this.emulateTagAuto(tags.map((t) => ({ epc: t })))
  }

  async emulateTagAuto(rfidTags: Partial<TmrTag> | Partial<TmrTag>[], skipStopCallbacks = false) {
    this.onStartCallback?.()
    this.onStartCallbackAntennaButton?.()
    if (Array.isArray(rfidTags)) {
      rfidTags.forEach((rfidTag) => this.onTagRead(JSON.stringify(rfidTag)))
    } else {
      this.onTagRead(JSON.stringify(rfidTags))
    }

    if (!skipStopCallbacks) {
      setTimeout(async () => {
        this.onStopCallback?.()
        this.onStopCallbackAntennaButton?.()
      }, 1000)
    }
  }

  itemFromTag(tag: TmrTag): { identifiers: Partial<ItemIdentifier>[] } {
    return { identifiers: [{ id: tag.epc, code: tag.epc, type: 'TAG' }] }
  }

  addTidToItems = (tags, items) => {
    tags.map((tag) => {
      if (tag.epc && tag.tid) {
        if (items[tag.epc]?.item) {
          items[tag.epc].item.readTid = tag.tid
          items[tag.epc].item.retrieveTid = items[tag.epc].itemIdentifier
            ? items[tag.epc].itemIdentifier.tid
            : undefined
        }
      }
    })
    return items
  }

  decodeTags = debounce(
    () => {
      const tagsToDecode = this.tags.filter((t) => t.pending && !t.processing)
      if (tagsToDecode.length == 0) return
      tagsToDecode.forEach((t) => (t.processing = true))
      const { decodeFunction } = this
      if (decodeFunction) {
        decodeFunction(
          tagsToDecode.map((tag) => tag.epc ?? tag.uid),
          tagsToDecode.map((tag) => (tag.epc ? 'UHF_TAG' : tag.uid ? 'NFC_TAG' : undefined)),
          tagsToDecode.map((tag) => tag.antennaId)
        )
          .then((result) => {
            const itms = this.addTidToItems(tagsToDecode, result)
            this.onDecodedItemCallback?.(itms, tagsToDecode)
          })
          .catch((e) => {
            console.error(e)
          })
          .finally(() => {
            this.onPendingTagsChangeAntennaButton?.(0)
            tagsToDecode.forEach((tag) => {
              tag.pending = false
              tag.processing = false
            })
          })
        return
      }
      if (!this.decodeRequest?.url) throw new Error('decodeUrl not set')

      const fields = this.decodeRequest.epcFields || 'identifierCodes'
      const paramDecode = {
        ...this.decodeRequest.payload
      }
      paramDecode[fields] = tagsToDecode.map((tag) => tag.epc ?? tag.uid)
      Items.decode(this.decodeRequest.url, paramDecode)
        .then((items) => {
          const itms = this.addTidToItems(tagsToDecode, items)
          this.onDecodedItemCallback?.(itms, tagsToDecode)
        })
        .catch((err) => {
          showToastError(err)
        })
        .finally(() => {
          tagsToDecode.forEach((tag) => {
            tag.pending = false
            tag.processing = false
          })
          this.onPendingTagsChangeAntennaButton?.(this.tags.filter((t) => t.pending).length)
        })
    },
    this.debounceDecodeTime || 300,
    { maxWait: 2000 }
  )

  onTagRead(stringTagData: string, antennaId?: string) {
    if (!this.avoidRestartingTimerOnRead) this.startTimer()
    let tag: TmrTag
    try {
      tag = JSON.parse(stringTagData)
      const tagExists = this.tags.some((t) =>
        tag.uid && tag.uid !== '' ? tag.uid === t.uid : tag.epc && tag.epc !== '' ? tag.epc === t.epc : false
      )
      if (tagExists) {
        return
      }

      if (!tag.uid && !tag.epc) {
        return
      }

      if (this.ignoreReadingTagsWithPrefixes.length > 0 && tag.epc && tag.epc !== null) {
        //si controlla che tag non inizi con uno dei prefissi impostati
        let validStartEpc = true
        this.ignoreReadingTagsWithPrefixes.forEach((str) => {
          if (tag.epc.startsWith(str)) {
            validStartEpc = false
          }
        })

        if (!validStartEpc) {
          return
        }
      }

      if (this.onTagReadCallback) {
        this.tags.push({ ...tag, pending: false, processing: false, antennaId })
        this.onTagReadCallback(tag, antennaId)
      } else if (this.batchInterval) {
        this.tags.push({ ...tag, pending: false, processing: false, antennaId })
        this.manageBatchTags(tag)
        this.startBatchTimer(false)
      } else {
        this.tags.push({ ...tag, pending: true, processing: false, antennaId })
        this.onPendingTagsChangeAntennaButton?.(this.tags.filter((t) => t.pending).length)
        this.decodeTags()
      }
    } catch (err) {
      console.error(err)
      return
    }
  }

  manageBatchTags = (tag) => {
    const index = this.batchIntervalTags.findIndex((t) => !t.processing && t.tags.length < this.batchIntervalTagCount)
    if (index === -1) {
      this.batchIntervalTags.push({
        processing: false,
        completed: false,
        tags: [{ ...tag, pending: true }]
      })
    } else {
      this.batchIntervalTags[index].tags.push({ ...tag, pending: true })
    }
  }

  startBatchTimer = (destroy) => {
    if (destroy) {
      this.stopBatchTimer()
    }
    if (!this.batchIntervalTimer) {
      const index = this.batchIntervalTags.findIndex((t) => !t.completed && !t.processing)
      if (index !== -1) {
        this.batchIntervalTimer = setTimeout(() => {
          this.processedBatch()
        }, this.batchIntervalTime)
      }
    }
  }

  stopBatchTimer = () => {
    if (!this.batchIntervalTimer) return
    clearTimeout(this.batchIntervalTimer)
    this.batchIntervalTimer = undefined
  }

  processedBatch = () => {
    const index = this.batchIntervalTags.findIndex((t) => !t.completed && !t.processing)
    if (index !== -1) {
      this.batchIntervalTags[index].processing = true
      const tagsToDecode = this.batchIntervalTags[index].tags.filter((t) => t.pending && !t.processing)
      if (tagsToDecode.length == 0) {
        this.batchIntervalTags[index].completed = true
        this.startBatchTimer(true)
        return
      }
      tagsToDecode.forEach((t) => (t.processing = true))
      const { decodeFunction } = this
      if (decodeFunction) {
        decodeFunction(
          tagsToDecode.map((tag) => tag.epc ?? tag.uid),
          tagsToDecode.map((tag) => (tag.epc ? 'UHF_TAG' : tag.uid ? 'NFC_TAG' : undefined)),
          tagsToDecode.map((tag) => tag.antennaId)
        )
          .then((result) => {
            this.onDecodedItemCallback?.(result, tagsToDecode)
          })
          .catch((e) => {
            this.batchIntervalTags[index].processing = false
            console.error(e)
          })
          .finally(() => {
            this.batchIntervalTags[index].completed = true
            this.onPendingTagsChangeAntennaButton?.(0)
            tagsToDecode.forEach((tag) => {
              tag.pending = false
            })
            this.startBatchTimer(true)
          })
        return
      }
      if (!this.decodeRequest?.url) throw new Error('decodeUrl not set')

      const fields = this.decodeRequest.epcFields || 'identifierCodes'
      const paramDecode = {
        ...this.decodeRequest.payload
      }
      paramDecode[fields] = tagsToDecode.map((tag) => tag.epc ?? tag.uid)
      Items.decode(this.decodeRequest.url, paramDecode)
        .then((items) => {
          this.onDecodedItemCallback?.(items, tagsToDecode)
        })
        .catch((err) => {
          this.batchIntervalTags[index].processing = false
          showToastError(err)
        })
        .finally(() => {
          this.batchIntervalTags[index].completed = true
          tagsToDecode.forEach((tag) => {
            tag.pending = false
          })
          this.onPendingTagsChangeAntennaButton?.(this.getBatchPendingTagCount())
          this.startBatchTimer(true)
        })
    }
  }

  getBatchPendingTagCount = () => {
    let count = 0
    for (const element of this.batchIntervalTags) {
      count += element.tags.filter((t) => t.pending).length
    }
    return count
  }

  async fillRfidAntennas(workstation: TmrWorkstation) {
    if (!workstation.antennas) {
      const tmpAntennas = await Promise.all(
        workstation.rfidAntennaIds.map(async (antennaId) => {
          return RfidAntennas.get<TmrRfidAntenna>(antennaId).catch(function (err) {
            return undefined
          })
        })
      )

      const rfidAntennas: TmrRfidAntenna[] = []
      tmpAntennas.forEach((antenna) => {
        if (antenna?.id) rfidAntennas.push(antenna)
      })
      workstation.antennas = rfidAntennas
    }
  }

  async fillBalance(workstation: TmrWorkstation) {
    if (!workstation.balance && !!workstation.balanceId) {
      try {
        const balance = await Balances.get<TmrBalance>(workstation.balanceId)
        workstation.balance = balance
      } catch (error) {
        console.error('Cannot find balance', error)
      }
    }
  }

  getWorkstationUserAntennas = async () => {
    if (!this.workstation) return []
    const antennas = this.workstation.antennas
    const newAntennas: TmrRfidAntenna[] = await Promise.all(
      antennas.map(async (antenna): Promise<TmrRfidAntenna> => {
        const tmp: TmrRfidAntenna = _cloneDeep(antenna)

        const WriteStorageKey = `Settings-${this.workstation.id}-${antenna.id}-txPower`
        const power = _get(tmp, 'txPower')
        const currentPower = Storage.load(WriteStorageKey, power)
        tmp.txPower = currentPower

        if (antenna.reader.deviceType !== 'mediol-40') {
          const SensitivityStorageKey = `Settings-${this.workstation.id}-${antenna.id}-rxSensitivity`
          const sensitivity = _get(tmp, 'rxSensitivity')
          const currentSensitivity = Storage.load(SensitivityStorageKey, sensitivity)
          tmp.rxSensitivity = currentSensitivity
        }
        return tmp
      })
    )
    return newAntennas
  }

  async closeAnyWebSocket() {
    if (this.webSocket && this.webSocket.length > 0) {
      let ws: any
      for (const element of this.webSocket) {
        if (element) {
          ws = element
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            await ws.close()
          }
        }
      }
    }
  }

  getAntennas() {
    if (this.workstation) {
      const antennas = this.workstation?.antennas ?? []
      if (this.tagTypeToCheck === 'ALL') {
        return antennas
      }
      // antennas[].reader?.settings?.supportedTagType potrebbe non essere settato in configurazioni vecchie
      return antennas.filter((a) => a.reader?.settings?.supportedTagType === this.tagTypeToCheck)
    }
    return []
  }

  is_skip_antenna = (type: 'start' | 'stop', antenna: TmrRfidAntenna, deviceType?: string) => {
    if (deviceType && antenna && antenna.reader.deviceType !== deviceType) return true
    if (type === 'stop' && this.excludedAntennaIdsFromStopRequest.includes(antenna.id)) return true
    return false
  }

  check_antenna_status = (type: 'start' | 'stop') => {
    let executeCall = true
    let wsAntenna: any
    /*
     * check antenna's readyState based on the type to avoid making unnecessary calls
     */
    if (this.webSocket) {
      wsAntenna = this.webSocket[this.current_antenna]
      if (type === 'stop' && !wsAntenna) {
        executeCall = false
        if (this.ws_configurations[this.current_antenna]) {
          this.ws_configurations[this.current_antenna] = 'stopped'
        } else {
          this.ws_configurations.push('stopped')
        }
      } else if (
        type === 'start' &&
        wsAntenna &&
        (wsAntenna.readyState === WebSocket.OPEN || wsAntenna.readyState === WebSocket.CONNECTING)
      ) {
        executeCall = false
      }
    }
    return { executeCall, wsAntenna }
  }

  get_antenna_api_data = (
    type: 'start' | 'stop',
    antenna: TmrRfidAntenna,
    antennasToOperate,
    customCommandsOperationType?: string
  ) => {
    const antennaPayload: any = {
      ...antenna.reader,
      id: antenna.reader.code,
      antennas: antennasToOperate
    }
    const isCustomCommandOperation =
      [...READERS_SUPPORTING_CUSTOM_COMMANDS, ...READERS_SUPPORTING_KILL_TAG].includes(antenna.reader.deviceType) &&
      customCommandsOperationType
    const operationTypeParam = isCustomCommandOperation ? `operationType=${customCommandsOperationType}&` : ''
    let param = `${operationTypeParam}sessionId=${this.sessionId}`
    if (type === 'start') {
      param += '&memoryBank=TID&address=0&numBlocks=6'
      const readNfc = this.nfcReadConfiguration
      if (readNfc?.active) {
        param += '&user=true'
        if (readNfc.block !== undefined && !Number.isNaN(readNfc.block)) {
          param += `&block=${readNfc.block}`
        }
        if (readNfc.blockCount !== undefined && !Number.isNaN(readNfc.blockCount)) {
          param += `&blockCount=${readNfc.blockCount}`
        }
      }
    }

    return {
      antennaPayload,
      param,
      operationPath: isCustomCommandOperation ? `${type}-custom-operation` : type
    }
  }

  stop_antenna_websocket = (wsAntenna: WebSocket | undefined): boolean => {
    if (this.ws_configurations[this.current_antenna]) {
      this.ws_configurations[this.current_antenna] = 'stopped'
    } else {
      this.ws_configurations.push('stopped')
    }
    if (wsAntenna && (wsAntenna.readyState === WebSocket.OPEN || wsAntenna.readyState === WebSocket.CONNECTING)) {
      wsAntenna.close()
    }
    return true
  }

  start_antenna_websocket = (antenna: TmrRfidAntenna, onStop?: () => void): boolean => {
    const wsUrl = `${this.workstation?.deviceManagerWebsocket}/rfidAntennas/scannings?antennaId=${antenna.id}`
    if (this.ws_configurations[this.current_antenna]) {
      this.ws_configurations[this.current_antenna] = wsUrl
    } else {
      this.ws_configurations.push(wsUrl)
    }
    const webSocket = new WebSocket(this.ws_configurations[this.current_antenna])
    webSocket.onmessage = (ev) => this.onTagRead(ev.data, antenna.id)
    webSocket.onerror = (_ev) => {
      AppStore.sendDataDogLog('WebSocket Antenna Error', {})
      onStop ? onStop() : this.stopWebSocket()
    }
    webSocket.onclose = (ev) => {
      AppStore.sendDataDogLog('WebSocket Antenna Close', { message: ev.reason, code: ev.code })
      if (onStop) {
        onStop()
      } else if (!this.isManualStop) {
        this.stopWebSocket()
      }
    }
    this.webSocket?.push(webSocket)
    this.detailsStatusAntennas.push(__(T.error.antenna_was_started, { code: antenna.code }))
    return true
  }

  async turn_process_antenna(
    type: 'start' | 'stop',
    onStop?: () => void,
    deviceType?: string,
    customCommandsOperationType?: string
  ) {
    if (!this.workstation) {
      this.startErrorAntenna(type)
      return false
    }

    const antennas = this.getAntennas()
    const userAntennas = await this.getWorkstationUserAntennas()
    const antenna = antennas[this.current_antenna]

    try {
      if (this.is_skip_antenna(type, antenna, deviceType)) return true

      const { executeCall, wsAntenna } = this.check_antenna_status(type)
      if (!executeCall) return true

      const antennasToOperate = userAntennas.filter(
        (a) => type === 'start' || !this.excludedAntennaIdsFromStopRequest.includes(a.id)
      )
      if (antennasToOperate.length === 0) return []

      const { antennaPayload, param, operationPath } = this.get_antenna_api_data(
        type,
        antenna,
        antennasToOperate,
        customCommandsOperationType
      )
      const response = await this.getDeviceManagerApi().post(
        `${ANTENNA_PATH}${antenna.id}/${operationPath}?${param}`,
        antennaPayload,
        { timeout: DEVICE_MANAGER_ANTENNA_TIMEOUT }
      )

      if (!response.ok) throw new Error(__(T.error.antenna_was_not_started, { code: antenna.code }))

      if (type === 'start') return this.start_antenna_websocket(antenna, onStop)
      return this.stop_antenna_websocket(wsAntenna)
    } catch {
      this.detailsStatusAntennas.push(__(T.error.antenna_was_not_started, { code: antenna?.code ?? 'N/A' }))
      this.startErrorAntenna(type)
      return false
    }
  }

  async startErrorAntenna(type: 'start' | 'stop') {
    if (type === 'start') {
      this.ws_configurations = []
    }
  }

  async turn_process(
    type: 'start' | 'stop',
    onStop?: () => void,
    deviceType?: string,
    customCommandsOperationType?: string
  ) {
    this.current_antenna++
    if (this.workstation && this.current_antenna < this.getAntennas().length) {
      const res = await this.turn_process_antenna(type, onStop, deviceType, customCommandsOperationType)
      if (res && this.ws_configurations.length > 0) {
        return this.turn_process(type, onStop, deviceType, customCommandsOperationType)
      }
    }
    return this.ws_configurations
  }

  private async handle_start_failed() {
    if (this.restartAntennas) return
    if (!AppStore.getEmulation()) {
      const details = this.detailsStatusAntennas.length > 0 ? this.detailsStatusAntennas.join('\n') : undefined
      const error = new SystemError(
        500,
        details ?? 'Internal Server Error',
        __(T.error.error),
        __(T.error.workstation_could_not_be_started, { code: this.workstation?.code })
      )
      showToastError(error, __(T.error.error))
      Sounds.error()
    }
    await this.stop({ manual: true })
  }

  async turn_sequential(
    type: 'start' | 'stop' = 'start',
    onStop?: () => void,
    deviceType?: string,
    customCommandsOperationType?: string
  ) {
    this.current_antenna = -1
    if (type === 'start') {
      this.webSocket = []
      this.ws_configurations = []
      this.detailsStatusAntennas = []
    }
    if (!this.workstation) return undefined
    await this.fillRfidAntennas(this.workstation)
    await this.turn_process(type, onStop, deviceType, customCommandsOperationType)
    if (type === 'start') {
      if (this.ws_configurations.length === 0) {
        await this.handle_start_failed()
      } else {
        Sounds.scan()
      }
    }
    return this.ws_configurations
  }

  async start(options: RfidReaderStartOptions = {}): Promise<boolean> {
    const {
      onTagReadCallback,
      onStop,
      connectionFailedCallback,
      restart = false,
      tagTypeToCheck = 'ALL',
      customCommandsOperationType
    } = options

    if (this.startInProgressPromise !== undefined) {
      return this.startInProgressPromise
    }

    this.startInProgressPromise = (async () => {
      if (this.restart_onerror_counter === this.restart_onerror_max - 1) {
        this.restart_onerror_counter = 0
      }
      this.excludedAntennaIdsFromStopRequest = []
      this.restartAntennas = restart
      this.connectionFailedCallback = connectionFailedCallback
      if (onTagReadCallback) this.onTagReadCallback = onTagReadCallback
      if (tagTypeToCheck) this.tagTypeToCheck = tagTypeToCheck

      const wsConfigurations = await this.turn_sequential('start', onStop, undefined, customCommandsOperationType)
      if (!wsConfigurations || wsConfigurations.length === 0) {
        // check if restart antennas
        if (this.restartAntennas) {
          await this.restartAutomaticAntennas()
          return false
        }

        if (this.connectionFailedCallback) {
          this.connectionFailedCallback()
        }

        return false
      }

      this.onStartCallback?.()
      this.onStartCallbackAntennaButton?.()

      this.restartAntennas = false
      this.restart_onerror_counter = 0
      this.wakeUpSocket()
      this.startTimer()
      return true
    })()

    try {
      return await this.startInProgressPromise
    } finally {
      this.startInProgressPromise = undefined
    }
  }

  restartAutomaticAntennas = async () => {
    setTimeout(async () => {
      this.restart_onerror_counter++
      if (this.restart_onerror_counter < this.restart_onerror_max) {
        await this.start({
          onTagReadCallback: this.onTagReadCallback,
          connectionFailedCallback: this.connectionFailedCallback,
          restart: this.restart_onerror_counter < this.restart_onerror_max - 1,
          tagTypeToCheck: this.tagTypeToCheck ?? 'ALL'
        })
      } else {
        // show toast for antenna not started
      }
    }, 1000)
  }

  stopWebSocket = async (restart = false) => {
    await this.stop({ manual: false })
    if (restart) {
      this.restartAntennas = restart
      await this.restartAutomaticAntennas()
    }
  }

  stop = async (options: RfidReaderStopOptions = {}): Promise<boolean> => {
    const { manual = true, automatic = false, deviceType, customCommandsOperationType } = options
    this.isManualStop = manual

    const stopped = await this.turn_sequential('stop', undefined, deviceType, customCommandsOperationType)
    this.ws_configurations = []

    this.webSocket = undefined
    this.onStopCallback?.(automatic)
    this.onStopCallbackAntennaButton?.()
    this.resetWakeupTimeout()
    this.stopTimer()
    if (manual) {
      this.resetManualStop()
    }

    if (
      stopped &&
      this.workstation &&
      this.getAntennas().length === stopped.filter((elem) => elem === 'stopped').length
    ) {
      return true
    }

    return false
  }

  resetManualStop = (delayMs: number = 300) => {
    setTimeout(() => {
      this.isManualStop = false
    }, delayMs)
  }

  resetWakeupTimeout = () => {
    if (this.wakeUpSocketTimeout) {
      clearInterval(this.wakeUpSocketTimeout)
      this.wakeUpSocketTimeout = undefined
    }
  }

  wakeUpSocket = () => {
    this.resetWakeupTimeout()
    this.wakeUpSocketTimeout = setTimeout(() => {
      if (this.isReading()) {
        this.webSocket?.forEach((ws) => {
          if (ws && !([WebSocket.CLOSED, WebSocket.CLOSING] as number[]).includes(ws.readyState)) {
            ws.send('{}')
          }
        })
        this.wakeUpSocket()
      } else {
        this.webSocket?.forEach((ws) => ws?.readyState === WebSocket.OPEN && ws.close())
        this.webSocket = undefined
      }
    }, 3000) as any
  }

  getAntenna = (tagType: 'NFC' | 'UHF' = 'UHF') => {
    let antenna
    if (this.workstation) {
      const antennas = this.workstation?.antennas ?? []
      antenna = antennas.find(
        (a) => a.reader.settings.supportedTagType === tagType && a.reader.settings.writeEnabled === 'true'
      )
      if (!antenna) {
        antenna = antennas.find((a) => WRITE_DEVICE_TYPE_READER.has(a.reader.deviceType))
      }
    }
    return antenna
  }

  /**
   * Device manager client, created on first use and reused until the workstation (and so the base url) changes.
   * A call needing a different timeout than the default must pass it on the single request,
   * e.g. `post(url, payload, { timeout: DEVICE_MANAGER_ANTENNA_TIMEOUT })`.
   */
  getDeviceManagerApi = () => {
    const baseURL = `${this.workstation?.deviceManagerUrl}`
    if (!this.deviceManagerApi || this.deviceManagerApiUrl !== baseURL) {
      this.deviceManagerApi = create({ baseURL: baseURL, timeout: DEVICE_MANAGER_TIMEOUT })
      this.deviceManagerApiUrl = baseURL
    }
    return this.deviceManagerApi
  }

  /** antennas as sent to the device manager, optionally energized with the configured write power */
  private getPayloadAntennas = (useWriteTxPower?: boolean): TmrRfidAntenna[] => {
    const antennas = this.workstation?.antennas ?? []
    if (!useWriteTxPower) return antennas
    return antennas.map((at) => ({
      id: at.id,
      creationDate: at.creationDate,
      lastModifyDate: at.lastModifyDate,
      code: at.code,
      description: at.description,
      zoneId: at.zoneId,
      reader: at.reader,
      readerPort: at.readerPort,
      txPower: at.writeTxPower ?? at.txPower,
      rxSensitivity: at.rxSensitivity
    }))
  }

  /** retries `attempt` with every configured password until one succeeds (single empty password when none is set) */
  private tryWithPasswords = async (
    password: string | string[] | undefined,
    attempt: (singlePassword: string) => Promise<any>
  ) => {
    const configuredPasswords = Array.isArray(password) ? password.filter((p) => !!p) : password ? [password] : []
    const passwordsToTry = configuredPasswords.length > 0 ? configuredPasswords : ['']
    let res: any = { ok: false }
    for (const singlePassword of passwordsToTry) {
      res = await attempt(singlePassword)
      if (res?.ok) return res
    }
    return res
  }

  /**
   * Single entry point for the device manager tag operations (read/write/lock/kill): resolves the antenna,
   * builds endpoint and payload and tracks the call on datadog. Response handling is left to the callers.
   */
  private tagOperation = async (config: TagOperationConfig) => {
    const antenna = this.getAntenna(config.tagType)
    if (!antenna) throw new Error(config.antennaError ?? 'antennaUHF not set')

    const { reader } = antenna
    const api = this.getDeviceManagerApi()

    const queryParam = Object.entries(config.queryParams)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => `${key}=${value}`)
      .join('&')

    const payload: any = {
      ...reader,
      id: reader.code,
      antennas: this.getPayloadAntennas(config.useWriteTxPower)
    }

    const endpoint = `${ANTENNA_PATH}${antenna.id}/${config.operation}?${queryParam}`
    const payloadDataDog: DatadogEncodingRunDeviceManager = {
      url: endpoint,
      payload: payload
    }

    const trackDatadog = (res: any, error: any, apiDurationMs: number) => {
      const { datadogSession } = config
      if (!datadogSession || AppStore.getEmulation()) return
      if (error) payloadDataDog.error = error
      else payloadDataDog.response = res
      datadogSession.deviceManager = payloadDataDog
      datadogRum.addAction(config.datadogAction, {
        ...datadogSession,
        ...config.datadogAttributes?.({ apiDurationMs, ok: !error && !!res?.ok })
      })
    }

    const requestStart = Date.now()
    try {
      const res: any = await api.post<{ value: string }>(endpoint, payload)
      trackDatadog(res, undefined, Date.now() - requestStart)
      return res
    } catch (e) {
      trackDatadog(undefined, e, Date.now() - requestStart)
      throw e
    }
  }

  /** query string shared by the write and lock operations */
  private writeQueryParams = (
    target: string,
    data: string,
    password?: string,
    useTidToWrite?: boolean,
    numBlocks = Math.ceil(data.length / 4)
  ) => ({
    sessionId: this.sessionId,
    memoryBank: 'EPC',
    address: 2,
    targetEpc: target,
    data: data,
    numBlocks: numBlocks,
    password: password,
    targetType: useTidToWrite ? 'TID' : undefined //&beepOnError=true&beepOnSuccess=true
  })

  /** a tag is considered read/locked when the device manager echoes back the requested epc */
  private targetEpcResult = (res: any, target: string): { ok: boolean; value?: string } => {
    const value = res?.data?.value
    const ok =
      (res?.ok && value && value === target) || (AppStore.getEmulation() && !isProductionEnvironment()) ? true : false
    return { ok, value }
  }

  readEpc = async (
    target: string,
    datadogSession?: DatadogEncodingRun,
    datadogAction?: string
  ): Promise<{ ok: boolean; value?: string }> => {
    const res = await this.tagOperation({
      operation: 'read',
      queryParams: {
        sessionId: this.sessionId,
        memoryBank: 'EPC',
        address: 2,
        targetEpc: target,
        numBlocks: Math.ceil(target.length / 4)
      },
      datadogSession: datadogSession,
      datadogAction: datadogAction ?? 'readEpc',
      datadogAttributes: ({ apiDurationMs, ok }) => ({
        read: { apiDurationMs, ok, requestedEpc: target }
      })
    })
    return this.targetEpcResult(res, target)
  }

  writeEpc = async (
    target: string,
    data: string,
    password?: string | string[],
    useTidToWrite?: boolean,
    datadogSession?: DatadogEncodingRun,
    datadogInfo?: { action?: string; writeRetryCount?: number; writeTagStartTime?: Date }
  ) =>
    this.tryWithPasswords(password, (singlePassword) =>
      this.writeEpcSingle(target, data, singlePassword, useTidToWrite, datadogSession, datadogInfo)
    )

  private async writeEpcSingle(
    target: string,
    data: string,
    password?: string,
    useTidToWrite?: boolean,
    datadogSession?: DatadogEncodingRun,
    datadogInfo?: { action?: string; writeRetryCount?: number; writeTagStartTime?: Date }
  ) {
    const writeRetryCount = datadogInfo?.writeRetryCount ?? 0
    const requestStart = Date.now()
    const offsetFromFirstWriteMs =
      datadogInfo?.writeTagStartTime && writeRetryCount > 0
        ? requestStart - datadogInfo.writeTagStartTime.getTime()
        : 0

    const res = await this.tagOperation({
      operation: 'write',
      queryParams: this.writeQueryParams(target, data, password, useTidToWrite),
      useWriteTxPower: true,
      datadogSession: datadogSession,
      datadogAction: datadogInfo?.action ?? 'writeEpc',
      datadogAttributes: ({ apiDurationMs, ok }) => ({
        write: { apiDurationMs, success: ok, offsetFromFirstWriteMs, writeRetryCount: writeRetryCount }
      })
    })

    if (AppStore.getEmulation() && !res.ok && !isProductionEnvironment()) {
      return { ok: false }
    }
    return res
  }

  lockTag = async (
    target: string,
    data: string,
    password?: string | string[],
    useTidToWrite?: boolean,
    datadogSession?: DatadogEncodingRun
  ) =>
    this.tryWithPasswords(password, (singlePassword) =>
      this.lockTagSingle(target, data, singlePassword, useTidToWrite, datadogSession)
    )

  lockTagSingle = async (
    target: string,
    data: string,
    password?: string,
    useTidToWrite?: boolean,
    datadogSession?: DatadogEncodingRun
  ): Promise<{ ok: boolean; value?: string }> => {
    const res = await this.tagOperation({
      operation: 'lock',
      queryParams: this.writeQueryParams(target, data, password, useTidToWrite, Math.ceil(target.length / 4)),
      datadogSession: datadogSession,
      datadogAction: 'lockTag',
      datadogAttributes: ({ apiDurationMs, ok }) => ({
        read: { apiDurationMs, success: ok, requestedEpc: target }
      })
    })
    return this.targetEpcResult(res, target)
  }

  killTag = async (
    target: string,
    password: string,
    tagType: 'NFC' | 'UHF' = 'UHF',
    datadogSession?: DatadogEncodingRun,
    datadogAction?: string
  ) => {
    const res = await this.tagOperation({
      operation: 'kill',
      tagType: tagType,
      queryParams: { sessionId: this.sessionId, targetEpc: target, password: password },
      useWriteTxPower: true,
      antennaError: 'antenna not set',
      datadogSession: datadogSession,
      datadogAction: datadogAction ?? 'killTag'
    })

    if (AppStore.getEmulation() && !res.ok && !isProductionEnvironment()) {
      return { ok: true }
    }
    return res
  }

  getWeight = async () => {
    if (!this.workstation) throw new Error('Workstation not set')
    await this.fillBalance(this.workstation)

    if (!this.workstation?.balance) throw new Error('Workstation balance not set')

    return this.getDeviceManagerApi().post<{ weight: number; response: string; error?: string }>(
      `/api/v1/balances/weight`,
      {
        ip: this.workstation.balance?.settings.ipAddress,
        port: this.workstation.balance?.settings.port
      }
    )
  }

  getWsCustomCommandsUrl = (antennaId?: string, kill: boolean = false) => {
    let url = ''
    if (this.workstation) {
      const readers = kill ? READERS_SUPPORTING_KILL_TAG : READERS_SUPPORTING_CUSTOM_COMMANDS
      const antenna = antennaId
        ? this.workstation.antennas.find((a) => a.id === antennaId)
        : this.workstation.antennas.find((a) => readers.includes(a.reader.deviceType))
      if (antenna) {
        url = `${this.workstation?.deviceManagerWebsocket}/rfidAntennas/commands?antennaId=${antenna.id}`
      }
    }
    return url
  }

  startWsCustomCommands = async (antennaId?: string, kill: boolean = false) => {
    if (!this.workstation) throw new Error('Workstation not set')
    const readers = kill ? READERS_SUPPORTING_KILL_TAG : READERS_SUPPORTING_CUSTOM_COMMANDS
    const antenna = antennaId
      ? this.workstation.antennas.find((a) => a.id === antennaId)
      : this.workstation.antennas.find((a) => readers.includes(a.reader.deviceType))
    if (!antenna) throw new Error('Only possible on readers: ' + readers.join(', '))

    const wsUrl = `${this.workstation?.deviceManagerWebsocket}/rfidAntennas/commands?antennaId=${antenna.id}`
    // returns the socket it opened: a caller that must not be disturbed by another one opened meanwhile
    // (customCommandsWs is shared) keeps sending on, and closing, its own
    return new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(wsUrl)
      this.customCommandsWs = ws
      ws.onopen = () => {
        if (!this.customCommandsWs) reject(new Error('WebSocket is not open'))
        ws.onerror = (ev) => {
          console.error(ev)
          ws.close()
          if (this.customCommandsWs === ws) this.customCommandsWs = undefined
        }
        resolve(ws)
      }
    })
  }

  resetWsCustomKeepAliveTimeout = () => {
    if (this.wsCustomKeepAliveTimeout) {
      clearInterval(this.wsCustomKeepAliveTimeout)
      this.wsCustomKeepAliveTimeout = undefined
    }
  }

  sendWsCustomKeepAlive = () => {
    this.resetWsCustomKeepAliveTimeout()
    this.wsCustomKeepAliveTimeout = setTimeout(() => {
      this.customCommandsWs?.send('{}')
      this.sendWsCustomKeepAlive()
    }, 3000) as any
  }

  stopWsCustomCommands = async (kill: boolean = false, antennaId?: string) => {
    this.resetWsCustomKeepAliveTimeout()
    this.customCommandsWs?.close()
    this.customCommandsWs = undefined
  }

  sendWsCustomCommandForKill: (command: any) => Promise<any> = (command) => {
    return new Promise((resolve, reject) => {
      if (this.customCommandsWs?.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not open'))
        return
      }
      this.customCommandsWs.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data)
          resolve(data)
        } catch {
          reject(new Error('Error parsing response: ' + ev.data))
        }
      }
      this.customCommandsWs.onerror = () => {
        reject(new Error('WebSocket error'))
      }
      this.customCommandsWs.send(JSON.stringify(command))
    })
  }

  /**
   * timeoutMs: reject with CustomCommandTimeoutError if no (successful) response arrives in time, e.g. the NFC tag
   * left the field. 0 = no timeout.
   * ws: the socket to use, by default the shared customCommandsWs (see startWsCustomCommands).
   */
  sendWsCustomCommandForResponse: (
    command: any,
    waitForSuccess?: boolean,
    timeoutMs?: number,
    ws?: WebSocket
  ) => Promise<any> = (command, waitForSuccess = true, timeoutMs = 0, ws = this.customCommandsWs) => {
    return new Promise((resolve, reject) => {
      if (ws?.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not open'))
        return
      }
      const timer = timeoutMs
        ? setTimeout(() => {
            // a late answer must not reach this command's handlers (nor resolve it)
            ws.onmessage = null
            ws.onerror = null
            reject(new CustomCommandTimeoutError('Custom command timeout'))
          }, timeoutMs)
        : undefined
      ws.onmessage = (ev) => {
        if (waitForSuccess) {
          try {
            const data = JSON.parse(ev.data)
            if (data.success) {
              clearTimeout(timer)
              resolve(data)
            }
          } catch (e) {
            clearTimeout(timer)
            reject(e instanceof Error ? e : new Error('Error parsing response from reader'))
          }
        } else {
          clearTimeout(timer)
          resolve(ev.data)
        }
      }
      ws.onerror = () => {
        clearTimeout(timer)
        reject(new Error('WebSocket error'))
      }
      ws.send(JSON.stringify(command))
    })
  }

  async getDeviceManagerStatus() {
    if (!this.workstation) throw new Error('Workstation not set')
    if (!this.workstation.attributes?.deviceManagerUpdateUrl) throw new Error('DeviceManagerUpdateUrl not set')

    this.deviceManagerStatusApi = create({
      baseURL: `${this.workstation.attributes?.deviceManagerUpdateUrl}`,
      timeout: 3000
    })
    return this.deviceManagerStatusApi.get<DeviceManagerStatus>('/api/v1/status')
  }

  async checkLastVersionAvailable(config: UpdateConfig) {
    if (!this.workstation) throw new Error('Workstation not set')
    if (!this.workstation.attributes?.deviceManagerUpdateUrl) throw new Error('DeviceManagerUpdateUrl not set')

    this.deviceManagerStatusApi = create({
      baseURL: `${this.workstation.attributes?.deviceManagerUpdateUrl}`,
      timeout: 3000
    })

    try {
      const response = await this.deviceManagerStatusApi.post<{ version: string }>(
        '/api/v1/last-version-available',
        config
      )
      return response
    } catch (error) {
      console.error('Error details:', error)
      throw error
    }
  }

  async updateDeviceManager(config: UpdateConfig) {
    if (!this.workstation) throw new Error('Workstation not set')
    if (!this.workstation.attributes?.deviceManagerUpdateUrl) throw new Error('DeviceManagerUpdateUrl not set')

    this.deviceManagerStatusApi = create({
      baseURL: `${this.workstation.attributes?.deviceManagerUpdateUrl}`,
      timeout: 3000
    })
    return this.deviceManagerStatusApi.post('/api/v1/update', config)
  }

  async restartDeviceManager() {
    if (!this.workstation) throw new Error('Workstation not set')
    if (!this.workstation.attributes?.deviceManagerUpdateUrl) throw new Error('DeviceManagerUpdateUrl not set')

    this.deviceManagerStatusApi = create({
      baseURL: `${this.workstation.attributes?.deviceManagerUpdateUrl}`,
      timeout: 3000
    })
    return this.deviceManagerStatusApi.post('/api/v1/restart')
  }

  private extractVersion(status: string): string {
    const match = /RUNNING\s+(\d+\.\d+\.\d+)/.exec(status)
    return match ? match[1] : ''
  }

  async checkForUpdates(
    config: UpdateConfig
  ): Promise<{ hasUpdate: boolean; currentVersion: string; latestVersion: string } | null> {
    try {
      // Se non c'è una workstation, proviamo a inizializzare
      if (!this.workstation) {
        await this.initialize()
        if (!this.workstation) return null
      }

      // Se siamo in modalità emulazione, usiamo dei dati mock
      if (AppStore.getEmulation()) {
        const mockCurrentVersion: string = '1.5.0'
        const mockLatestVersion: string = '1.5.0'
        return {
          hasUpdate: mockCurrentVersion !== mockLatestVersion,
          currentVersion: mockCurrentVersion,
          latestVersion: mockLatestVersion
        }
      }

      const statusResponse = await this.getDeviceManagerStatus()
      if (!statusResponse.ok || !statusResponse.data) {
        return null
      }

      const currentVersion = this.extractVersion(statusResponse.data.status)
      if (!currentVersion) {
        return null
      }

      const latestVersionResponse = await this.checkLastVersionAvailable(config)
      if (!latestVersionResponse.ok || !latestVersionResponse.data) {
        return null
      }

      // Estraiamo la versione dall'oggetto response
      const latestVersion = latestVersionResponse.data.version

      return {
        hasUpdate: currentVersion !== latestVersion,
        currentVersion,
        latestVersion
      }
    } catch (error) {
      console.log('Errore durante il controllo degli aggiornamenti:', error)
      return null
    }
  }

  async pollUpdateStatus(): Promise<{ success: boolean; mustReboot?: boolean; error?: string }> {
    if (!this.workstation) throw new Error('Workstation not set')

    if (AppStore.getEmulation()) {
      await new Promise((resolve) => setTimeout(resolve, 5000))
      return { success: true }
    }

    const pollInterval = 2000 // 2 secondi
    const initialVersion = this.currentVersion

    await new Promise((resolve) => setTimeout(resolve, 20000))

    while (true) {
      try {
        const statusResponse = await this.getDeviceManagerStatus()

        // Se torna RUNNING, controlliamo la versione
        if (statusResponse.data.status.includes('RUNNING')) {
          const currentVersion = this.extractVersion(statusResponse.data.status)

          // Se è tornato RUNNING ma con la vecchia versione:
          // o è fallito l'aggiornamento
          // o il DM non è riuscito a killare la versione precedente prima di avviare la nuova (la quale fallirà l'avvio per porta già occupata)
          // in entrambi i casi dobbiamo quindi dire all'utente di riavviare il sistema
          // (vedi task https://temera.atlassian.net/browse/STL5-4056)
          if (currentVersion === initialVersion) {
            return { success: true, mustReboot: true }
          }

          // Se è RUNNING con una nuova versione, successo!
          if (currentVersion !== initialVersion) {
            return { success: true }
          }
        }

        await new Promise((resolve) => setTimeout(resolve, pollInterval))
      } catch (error) {
        console.log('Polling error:', error)
        await new Promise((resolve) => setTimeout(resolve, pollInterval))
      }
    }
  }

  async performUpdate(config: UpdateConfig): Promise<{ success: boolean; mustReboot?: boolean; error?: string }> {
    if (!this.workstation) throw new Error('Workstation not set')

    // Se siamo in modalità emulazione, controlliamo shouldFail
    if (AppStore.getEmulation()) {
      await new Promise((resolve) => setTimeout(resolve, 5000)) // Aspettiamo 5 secondi

      // Controlliamo se è stato impostato shouldFail nei test
      const shouldFail = globalThis.__TEST_SHOULD_FAIL__ || false

      if (shouldFail) {
        return {
          success: false,
          error: "Errore durante l'aggiornamento: impossibile completare l'operazione"
        }
      }

      return { success: true }
    }

    // Salva la versione corrente per il polling
    const statusResponse = await this.getDeviceManagerStatus()
    this.currentVersion = this.extractVersion(statusResponse.data.status)

    // Avvia l'aggiornamento
    await this.updateDeviceManager(config)

    // Polling dello stato dell'aggiornamento
    const updateResult = await this.pollUpdateStatus()

    return updateResult
  }
}

export default new RfidReader()
